import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import type { ForumCategory } from "../types";

interface Props {
	selectedIds: string[];
	onChange: (ids: string[]) => void;
	allowSearch?: boolean;
	className?: string;
}

const CategoryMultiSelect: React.FC<Props> = ({
	selectedIds,
	onChange,
	allowSearch = true,
	className = "",
}) => {
	const [categories, setCategories] = useState<ForumCategory[]>([]);
	const [queryText, setQueryText] = useState("");
	const [loading, setLoading] = useState(true);
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const run = async () => {
			setLoading(true);
			try {
				const snap = await getDocs(collection(db, "forumCategories"));
				let rows = snap.docs.map((d) => ({
					id: d.id,
					...(d.data() as any),
				})) as ForumCategory[];
				// Fallback to defaults if collection empty to match ForumPage/Home
				if (!rows || rows.length === 0) {
					rows = [
						{
							id: "furniture-wood",
							name: "רהיטים ותיקוני עץ",
							description: "תיקון רהיטים, עבודות עץ ושיפוצים",
							iconName: "couch",
							postCount: 0,
						} as any,
						{
							id: "smartphones",
							name: "סמארטפונים",
							description: "תיקון מכשירי נייד",
							iconName: "phone",
							postCount: 0,
						} as any,
						{
							id: "electronics",
							name: "אלקטרוניקה כללית",
							description: "תיקון מכשירים אלקטרוניים",
							iconName: "wrench",
							postCount: 0,
						} as any,
						{
							id: "home-appliances",
							name: "מכשירי חשמל ביתיים",
							description: "תיקון מכשירי חשמל ביתיים",
							iconName: "bulb",
							postCount: 0,
						} as any,
						{
							id: "home-care",
							name: "טיפוח הבית",
							description: "ניקיון, תחזוקה, שיפוצים קלים וטיפוח הבית",
							iconName: "home",
							postCount: 0,
						} as any,
						{
							id: "car-transport",
							name: "רכב ותחבורה",
							description: "טיפים ותיקונים לרכב ומכשירי תחבורה",
							iconName: "car",
							postCount: 0,
						} as any,
						{
							id: "computers",
							name: "לפטופים ומחשבים",
							description: "תיקון מחשבים ניידים ונייחים",
							iconName: "laptop",
							postCount: 0,
						} as any,
					];
				}
				setCategories(rows);
			} catch (e) {
				console.error("load categories failed", e);
				// Fallback to defaults
				setCategories([
					{
						id: "smartphones",
						name: "סמארטפונים וטאבלטים",
						description: "תיקון וניהול מכשירי ניידים - מסך, סוללה, חומרה",
						iconName: "phone",
						postCount: 0,
					} as any,
					{
						id: "computers",
						name: "מחשבים נייחים ולפטופים",
						description: "בעיות חומרה ותוכנה במחשבים אישיים",
						iconName: "laptop",
						postCount: 0,
					} as any,
					{
						id: "home-appliances",
						name: "מכשירי חשמל ביתיים",
						description: "מקרר, תנור, מדיח, מכונת כביסה ועוד",
						iconName: "home",
						postCount: 0,
					} as any,
				]);
			} finally {
				setLoading(false);
			}
		};
		run();
	}, []);

	const filtered = useMemo(() => {
		if (!queryText.trim()) return categories;
		const q = queryText.trim().toLowerCase();
		return categories.filter(
			(c) =>
				c.name?.toLowerCase().includes(q) ||
				c.description?.toLowerCase().includes(q)
		);
	}, [categories, queryText]);

	const toggle = (id: string) => {
		const next = selectedIds.includes(id)
			? selectedIds.filter((x) => x !== id)
			: [...selectedIds, id];
		onChange(next);
	};

	// Close dropdown when clicking outside
	useEffect(() => {
		const handler = (e: MouseEvent) => {
			if (!rootRef.current) return;
			if (!rootRef.current.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	const selectedChips = selectedIds
		.map((id) => categories.find((c) => c.id === id)?.name)
		.filter(Boolean) as string[];

	const selectAll = () => onChange(categories.map((c) => c.id));
	const clearAll = () => onChange([]);

	return (
		<div ref={rootRef} className={`relative ${className}`}>
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-slate-100 flex items-center justify-between gap-2"
			>
				<div className="flex flex-wrap gap-1 items-center">
					{selectedChips.length === 0 ? (
						<span className="text-slate-400 text-sm">בחר קטגוריות...</span>
					) : (
						selectedChips.slice(0, 3).map((name, idx) => (
							<span
								key={`${name}-${idx}`}
								className="text-xs px-2 py-1 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-400/40"
							>
								{name}
							</span>
						))
					)}
					{selectedChips.length > 3 && (
						<span className="text-xs text-slate-400">
							+{selectedChips.length - 3}
						</span>
					)}
				</div>
				<span className="text-slate-400 text-xs">{open ? "סגור" : "פתח"}</span>
			</button>

			{open && (
				<div className="absolute z-[10002] mt-2 w-full rounded-md border border-slate-600 bg-slate-800 shadow-xl">
					{allowSearch && (
						<div className="p-2 border-b border-slate-700">
							<input
								type="text"
								value={queryText}
								onChange={(e) => setQueryText(e.target.value)}
								placeholder="חפש קטגוריות..."
								className="w-full rounded-md bg-slate-900 border border-slate-700 px-3 py-2 text-slate-100 placeholder-slate-400"
							/>
						</div>
					)}
					<div className="max-h-56 overflow-auto p-2">
						{loading ? (
							<div className="text-slate-400 text-sm p-2">טוען קטגוריות...</div>
						) : filtered.length === 0 ? (
							<div className="text-slate-400 text-sm p-2">
								לא נמצאו קטגוריות
							</div>
						) : (
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
								{filtered.map((c) => (
									<label
										key={c.id}
										className={`flex items-center gap-2 rounded-md px-3 py-2 border cursor-pointer transition ${
											selectedIds.includes(c.id)
												? "bg-cyan-500/10 border-cyan-400"
												: "bg-slate-900 border-slate-700 hover:bg-slate-900/60"
										}`}
									>
										<input
											type="checkbox"
											checked={selectedIds.includes(c.id)}
											onChange={() => toggle(c.id)}
											className="accent-cyan-400"
										/>
										<span className="text-slate-100 text-sm">{c.name}</span>
									</label>
								))}
							</div>
						)}
					</div>
					<div className="p-2 border-t border-slate-700 flex items-center justify-between text-xs text-slate-300">
						<span>נבחרו {selectedIds.length}</span>
						<div className="flex gap-2">
							<button
								type="button"
								className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100"
								onClick={clearAll}
							>
								נקה הכל
							</button>
							<button
								type="button"
								className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100"
								onClick={selectAll}
							>
								בחר הכל
							</button>
							<button
								type="button"
								className="px-2 py-1 rounded bg-cyan-500 hover:bg-cyan-400 text-slate-900"
								onClick={() => setOpen(false)}
							>
								סגור
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default CategoryMultiSelect;
